/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {ID, KTSVG} from '../../../../../../_dashboard/helpers'
import {useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {MenuComponent} from '../../../../../../_dashboard/assets/ts/components'
import {deleteOne} from '../../../../../services/common_requests'
import {toast} from 'react-toastify'
import {useQueryResponse} from '../../core/QueryResponseProvider'
import {Tooltip, OverlayTrigger} from 'react-bootstrap'
import {useAuth} from '../../../../../modules/auth'
import {isSuperAdmin} from '../../../../../utils/roleUtils'

type Props = {
  id: ID
  tourTitle?: string
}

const TourActionsCell: FC<Props> = ({id, tourTitle}) => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const {refetchData} = useQueryResponse()
  const {auth} = useAuth()
  const role = auth?.role || localStorage.getItem('role')
  const canDelete = isSuperAdmin(role)
  const expectedConfirm = (tourTitle || '').trim()
  const confirmMatches =
    !!expectedConfirm && confirmText.trim() === expectedConfirm

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  const handleEdit = () => {
    navigate(`/apps/tours/${id}`)
  }

  const closeDeleteModal = () => {
    setShowDeleteConfirm(false)
    setConfirmText('')
  }

  const handleDelete = async () => {
    if (!id) {
      toast.error('Tour ID is missing')
      return
    }
    if (!canDelete) {
      toast.error(
        intl.formatMessage({
          id: 'TOUR.DELETE_SUPERADMIN_ONLY',
          defaultMessage: 'Seuls les Super Admins peuvent supprimer un tour.',
        }),
      )
      return
    }
    if (!confirmMatches) {
      toast.error(
        intl.formatMessage({
          id: 'TOUR.DELETE_CONFIRM_MISMATCH',
          defaultMessage: 'Le nom du tour saisi ne correspond pas.',
        }),
      )
      return
    }

    try {
      setIsDeleting(true)
      await deleteOne(String(id) as unknown as ID, '/tours')
      closeDeleteModal()
      toast.success(
        intl.formatMessage({
          id: 'TOUR.DELETED_SUCCESSFULLY',
          defaultMessage: 'Tour has been deleted successfully',
        }),
      )
      refetchData()
    } catch (error) {
      toast.error(
        intl.formatMessage({
          id: 'TOUR.DELETE_ERROR',
          defaultMessage: 'Failed to delete tour. Please try again.',
        }),
      )
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <OverlayTrigger
        placement='top'
        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
      >
        <button className='btn btn-icon btn-light-primary btn-sm me-1' onClick={handleEdit}>
          <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
        </button>
      </OverlayTrigger>
      {canDelete && (
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
        >
          <button
            className='btn btn-icon btn-light-danger btn-sm'
            onClick={() => setShowDeleteConfirm(true)}
          >
            <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
          </button>
        </OverlayTrigger>
      )}

      {showDeleteConfirm && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          role='dialog'
          aria-modal='true'
          style={{backgroundColor: 'rgba(15, 23, 42, 0.55)', zIndex: 1055}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div
              className='modal-content border-0 shadow-lg'
              style={{borderRadius: 16, overflow: 'hidden'}}
            >
              <div
                className='px-6 pt-6 pb-4'
                style={{
                  background: 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 55%, #dc2626 100%)',
                  color: '#fff',
                }}
              >
                <div className='d-flex align-items-start justify-content-between gap-3'>
                  <div className='d-flex align-items-start gap-3'>
                    <span
                      className='d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                      style={{
                        width: 44,
                        height: 44,
                        backgroundColor: 'rgba(255,255,255,0.18)',
                      }}
                    >
                      <span className='material-symbols-outlined' style={{fontSize: 24}}>
                        warning
                      </span>
                    </span>
                    <div>
                      <h5 className='mb-1 fw-bold text-white'>
                        {intl.formatMessage({
                          id: 'TOUR.DELETE_IRREVERSIBLE_TITLE',
                          defaultMessage: 'Suppression définitive',
                        })}
                      </h5>
                      <div className='fs-7 text-white' style={{opacity: 0.9}}>
                        {intl.formatMessage({
                          id: 'TOUR.DELETE_IRREVERSIBLE_WARNING',
                          defaultMessage:
                            'Cette action est irréversible. Le tour et ses données associées seront définitivement supprimés.',
                        })}
                      </div>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon'
                    onClick={closeDeleteModal}
                    disabled={isDeleting}
                    style={{color: '#fff', opacity: 0.9}}
                    aria-label='Close'
                  >
                    <span className='material-symbols-outlined'>close</span>
                  </button>
                </div>
              </div>

              <div className='px-6 py-5'>
                <div
                  className='mb-4 px-4 py-3 rounded-3'
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div className='text-uppercase fs-8 fw-bold text-slate-500 mb-1'>
                    {intl.formatMessage({
                      id: 'TOUR.DELETE_TOUR_LABEL',
                      defaultMessage: 'Tour à supprimer',
                    })}
                  </div>
                  <div className='fw-bold text-gray-900 fs-5'>
                    {expectedConfirm || '—'}
                  </div>
                </div>

                <label className='form-label fw-semibold text-gray-800 mb-2'>
                  {intl.formatMessage(
                    {
                      id: 'TOUR.DELETE_TYPE_TOUR_NAME',
                      defaultMessage: 'Pour confirmer, saisissez le nom du tour « {name} » :',
                    },
                    {name: expectedConfirm || '…'},
                  )}
                </label>
                <input
                  type='text'
                  className='form-control form-control-lg'
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  disabled={isDeleting || !expectedConfirm}
                  placeholder={expectedConfirm}
                  autoFocus
                  style={{
                    borderColor: confirmText && !confirmMatches ? '#dc2626' : '#cbd5e1',
                    backgroundColor: '#fff',
                  }}
                />
                {!expectedConfirm && (
                  <div className='text-danger fs-8 mt-2'>
                    {intl.formatMessage({
                      id: 'TOUR.DELETE_MISSING_TITLE',
                      defaultMessage: 'Impossible de confirmer : le titre du tour est introuvable.',
                    })}
                  </div>
                )}
              </div>

              <div
                className='px-6 py-4 d-flex justify-content-end gap-2'
                style={{backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0'}}
              >
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={closeDeleteModal}
                  disabled={isDeleting}
                >
                  {intl.formatMessage({id: 'ACTION.CANCEL'})}
                </button>
                <button
                  type='button'
                  className='btn fw-semibold'
                  onClick={handleDelete}
                  disabled={isDeleting || !confirmMatches}
                  style={{
                    backgroundColor: confirmMatches ? '#b91c1c' : '#fecaca',
                    borderColor: confirmMatches ? '#b91c1c' : '#fecaca',
                    color: confirmMatches ? '#fff' : '#7f1d1d',
                    opacity: confirmMatches ? 1 : 0.7,
                  }}
                >
                  {isDeleting ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      <span className='ms-2'>
                        {intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}
                      </span>
                    </>
                  ) : (
                    intl.formatMessage({id: 'ACTION.DELETE'})
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export {TourActionsCell}
